import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Form } from "react-bootstrap";
import { isEmpty } from "lodash";
import { FileUploadField, capitalizeFirstLetter } from '../Shared/Helpers';
import CustomSweetAlert from 'components/Shared/CustomSweetAlert';

const StepThree = ({ handleNext, handleBack, storedData }) => {
    const apiURL = process.env.REACT_APP_JOB_URL;
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [alert, setAlert] = useState([]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (storedData) {
            setFormData(storedData?.bankDetails);
        }
    }, []);

    const validateForm = (formData) => {
        const errors = {
            employeeName: isEmpty(formData?.employeeName) && 'Employee name is required',
            accountHolderName: isEmpty(formData?.accountHolderName) && 'Bank account is required',
            bankName: isEmpty(formData?.bankName) && 'Bank name is required',
            bankAddress: isEmpty(formData?.bankAddress) && 'Bank address is required',
            accountNo: isEmpty(formData?.accountNo) && 'Bank account number is required',
            sortCode: isEmpty(formData?.sortCode) && 'Bank sort code is required',
            accountProof: isEmpty(formData?.accountProof?.Base64) && 'Proof of bank account file is required',
        };
        return errors;
    }

    const handleSubmit = async () => {
        try {
            const errors = validateForm(formData);

            if (!Object.values(errors).some(Boolean)) {
                setErrors([]);
                setAlert(CustomSweetAlert({ type: "loading" }));
                const { employeeName, accountHolderName, bankName, bankAddress, accountNo, sortCode } = formData;
                const data = {
                    employeeName, accountHolderName, bankName, bankAddress, accountNo, sortCode,
                    accountProof: formData.accountProof.Base64 || ''
                }
                const url = `/HRInduction/SubmitBankDetails?inductionId=${storedData?.inductionId}&employeeId=${storedData?.employeeId}&pin=${storedData?.pin}`;
                const method = `post`;
                const response = await fetch(apiURL + url, {
                    method,
                    headers: [
                        ["Content-Type", "application/json"],
                        ["Content-Type", "text/plain"]
                    ],
                    body: JSON.stringify(data),
                });
                if (response.status === 200) {
                    handleNext(data);
                } else {
                    showErrorAlert(`Save Bank Details failed`);
                }
            } else {
                setErrors(errors);
                window.scrollTo(0, 0);
            }
        } catch (e) {
            showErrorAlert(`Something went wrong`);
        }
    }

    const handleFileUpload = (event) => {
        const newFile = event.target.files[0];
        const maxSize = 5 * 1024 * 1024; // 5 MB
        const name = event.target.name;
        if (newFile && newFile.size > maxSize) {
            alert("File size is too large. Maximum file size allowed is 5MB.");
            event.target.value = null; // clear the file input
            return;
        }

        const reader = new FileReader();

        reader.onload = function (event) {
            const base64String = btoa(
                new Uint8Array(event.target.result)
                    .reduce((data, byte) => data + String.fromCharCode(byte), '')
            );
            const documentFilename = newFile.name;
            setFormData({
                ...formData,
                [name]: {
                    Base64: base64String,
                    DocumentFilename: documentFilename
                }
            });
        };

        reader.readAsArrayBuffer(newFile);
    }

    const handleRemoveFile = (name) => {
        setFormData({
            ...formData,
            [name]: null
        });
    };

    const showErrorAlert = (message) => setAlert(CustomSweetAlert({
        type: "error", title: "Error", message: message,
        onConfirm: () => {
            hideAlert();
        }, showCancel: false
    }));

    const hideAlert = () => setAlert(null);

    return (
        <Row>
            {alert}
            <Col sm={12} md={12}>
                <h2 >3. Bank Details</h2>
                <p style={{ marginTop: '-15px' }}>Please fill out all required fields</p>
            </Col>
            <Col sm={12} md={6} className="my-1">
                <h5>Employee Name:<span className="star">*</span></h5>
                <Form.Group>
                    <Form.Control type="text"
                        value={formData?.employeeName}
                        onChange={(e) => {
                            const updatedValue = e.target.value;
                            setFormData(prevState => ({
                                ...prevState,
                                employeeName: capitalizeFirstLetter(updatedValue),
                            }));
                        }}
                        className={errors.employeeName ? "form-control is-invalid custom-input" : "form-control custom-input"}
                    />
                    {errors.employeeName && <Form.Text className="text-danger"> <span className="star">*</span> {errors.employeeName} </Form.Text>}
                </Form.Group>
            </Col>
            <Col sm={12} md={6} className="my-1">
                <h5 className="">Bank Account Holders name(s):<span className="star">*</span></h5>
                <Form.Group>
                    <Form.Control type="text"
                        value={formData?.accountHolderName}
                        onChange={(e) => {
                            const updatedValue = e.target.value;
                            setFormData(prevState => ({
                                ...prevState,
                                accountHolderName: capitalizeFirstLetter(updatedValue),
                            }));
                        }}
                        className={errors.accountHolderName ? "form-control is-invalid custom-input" : "form-control custom-input"}
                    />
                    {errors.accountHolderName && <Form.Text className="text-danger"> <span className="star">*</span> {errors.accountHolderName} </Form.Text>}
                </Form.Group>
            </Col>
            <Col sm={12} md={6} className="my-1">
                <h5 className="title text-capitalize">Name of bank:<span className="star">*</span></h5>
                <Form.Group>
                    <Form.Control type="text"
                        value={formData?.bankName}
                        onChange={(e) => {
                            const updatedValue = e.target.value;
                            setFormData(prevState => ({
                                ...prevState,
                                bankName: capitalizeFirstLetter(updatedValue),
                            }));
                        }}
                        className={errors.bankName ? "form-control is-invalid custom-input" : "form-control custom-input"}
                    />
                    {errors.bankName && <Form.Text className="text-danger"> <span className="star">*</span> {errors.bankName} </Form.Text>}
                </Form.Group>
            </Col>
            <Col sm={12} md={6} className="my-1">
                <h5 className="title text-capitalize">Address of bank:<span className="star">*</span></h5>
                <Form.Group>
                    <Form.Control type="text"
                        value={formData?.bankAddress}
                        onChange={(e) => {
                            const updatedValue = e.target.value;
                            setFormData(prevState => ({
                                ...prevState,
                                bankAddress: capitalizeFirstLetter(updatedValue),
                            }));
                        }}
                        className={errors.bankAddress ? "form-control is-invalid custom-input" : "form-control custom-input"}
                    />
                    {errors.bankAddress && <Form.Text className="text-danger"> <span className="star">*</span> {errors.bankAddress} </Form.Text>}
                </Form.Group>
            </Col>
            <Col sm={12} md={6} className="my-1">
                <h5 className="title text-capitalize">Bank account number:<span className="star">*</span></h5>
                <Form.Group>
                    <Form.Control type="text"
                        value={formData?.accountNo}
                        onChange={(e) => {
                            const updatedValue = e.target.value;
                            setFormData(prevState => ({
                                ...prevState,
                                accountNo: capitalizeFirstLetter(updatedValue),
                            }));
                        }}
                        className={errors.accountNo ? "form-control is-invalid custom-input" : "form-control custom-input"}
                    />
                    {errors.accountNo && <Form.Text className="text-danger"> <span className="star">*</span> {errors.accountNo} </Form.Text>}
                </Form.Group>
            </Col>
            <Col sm={12} md={6} className="my-1">
                <h5 className="title text-capitalize">Bank Sort Code:<span className="star">*</span></h5>
                <Form.Group>
                    <Form.Control type="text"
                        value={formData?.sortCode}
                        onChange={(e) => {
                            const updatedValue = e.target.value;
                            setFormData(prevState => ({
                                ...prevState,
                                sortCode: capitalizeFirstLetter(updatedValue),
                            }));
                        }}
                        className={errors.sortCode ? "form-control is-invalid custom-input" : "form-control custom-input"}
                    />
                    {errors.sortCode && <Form.Text className="text-danger"> <span className="star">*</span> {errors.sortCode} </Form.Text>}
                </Form.Group>
            </Col>
            {formData?.accountProof ? formData?.accountProof?.DocumentFilename : formData?.accountProofFilePath}
            <FileUploadField
                label="Proof of bank account:"
                name="accountProof"
                documentFilename={formData?.accountProof ? formData?.accountProof?.DocumentFilename : formData?.accountProofFilePath}
                handleFileUpload={handleFileUpload}
                handleRemoveFile={handleRemoveFile}
                error={errors.accountProof}
            />
            <Col sm={12} md={12} style={{ textAlign: 'left' }}>
                <h5 className='my-0'>Please attach a header of a bank statement or letter from the bank as proof of account details.</h5>
                <div className='d-flex justify-content-start'>
                    <h5 className=''>It must show:</h5>
                    <ul style={{ listStyleType: "none" }}>
                        <li><h5 className='text-capitalize'>Bank Name</h5></li>
                        <li><h5 >Account Holder Name</h5></li>
                        <li><h5 >Account Number</h5></li>
                        <li><h5 >Sort Code</h5></li>
                    </ul>
                </div>
            </Col>
            <Col sm={12} className="d-flex justify-content-between mt-3">
                <Button className="btn-lg btn-shadow btn-step" variamt="danger" onClick={handleBack} >Back</Button>
                <Button className="btn-lg btn-shadow btn-step" variant="success" onClick={handleSubmit} >Next Step</Button>
            </Col>
        </Row>


    );
};

export default StepThree;